import { EventCleanup } from "./EventCleanup";
import { Connections } from "./Connections";
import { FolderPicker } from "./FolderPicker";
import { Brand, logoUrl, platformName } from "./Brand";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  ArrowUpRight,
  Camera,
  Check,
  ChevronLeft,
  Cloud,
  Copy,
  FolderOpen,
  Grid2X2,
  HardDrive,
  Image,
  LayoutTemplate,
  LogOut,
  Monitor,
  Plus,
  Radio,
  RefreshCw,
  Search,
  Settings2,
  Upload,
  Wifi,
  X,
  Link,
  BookOpen,
} from "lucide-react";
import QRCode from "qrcode";
import { api, save, upload } from "./api";
import {
  blankEvent,
  templates,
  type Design,
  type EventConfig,
  type EventData,
  type Media,
} from "./types";
import { CapturePage } from "./Capture";
import { Wall, PublicPage } from "./Wall";
import { DesignEditor } from "./Editor";
import { CameraStation } from "./Camera";
function ErrorText({ message }: { message: string }) {
  return message ? (
    <div className="error" role="alert">
      {message}
    </div>
  ) : null;
}
export default function App() {
  const captureRoute = location.pathname.match(/^\/capture\/([\w-]+)$/);
  if (captureRoute) return <CapturePage token={captureRoute[1]} />;
  const route = location.pathname.match(/^\/(display|gallery)\/([\w-]+)$/);
  if (route)
    return (
      <PublicPage kind={route[1] as "display" | "gallery"} token={route[2]} />
    );
  return <Admin />;
}
function Admin() {
  const [session, setSession] = useState<{
      authenticated: boolean;
      setupRequired: boolean;
      role?: "super" | "event";
      eventId?: string;
    } | null>(null),
    [events, setEvents] = useState<EventData[]>([]),
    [view, setView] = useState("events"),
    [active, setActive] = useState<EventData | null>(null),
    [creating, setCreating] = useState(false),
    [message, setMessage] = useState(""),
    [error, setError] = useState(""),
    [settings, setSettings] = useState({
      baseUrl: location.origin,
      driveConfigured: false,
    });
  const refresh = async () => {
    try {
      const [nextEvents, nextSettings] = await Promise.all([
        api("/admin/events"),
        api("/admin/settings"),
      ]);
      setEvents(nextEvents);
      setSettings(nextSettings);
    } catch (e) {
      setError((e as Error).message);
    }
  };
  useEffect(() => {
    void api("/session")
      .then(setSession)
      .catch((e) => setError(e.message));
  }, []);
  useEffect(() => {
    if (!session?.authenticated) return;
    void refresh();

    const timer = setInterval(refresh, 10000);
    return () => clearInterval(timer);
  }, [session?.authenticated]);
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(""), 5500);
    return () => clearTimeout(timer);
  }, [message]);
  async function open(id: string) {
    try {
      setActive(await api(`/admin/events/${id}`));
      setCreating(false);
    } catch (e) {
      setMessage((e as Error).message);
    }
  }
  if (!session)
    return (
      <div className="public-message">
        <Brand />
        <p>{error || "Opening your control room…"}</p>
      </div>
    );
  if (!session.authenticated)
    return (
      <Login
        setup={session.setupRequired}
        onLogin={() => {
          void api("/session").then(setSession);
        }}
      />
    );
  const selectedEvent = active || undefined;
  return (
    <div className="admin-shell">
      <aside className="sidebar">
        <Brand />
        <div className="workspace-label">YOUR WORKSPACE</div>
        <nav>
          <button
            className={view === "events" ? "active" : ""}
            onClick={() => {
              setView("events");
              setActive(null);
              setCreating(false);
            }}
          >
            <Grid2X2 size={18} />
            Events<span>{events.length}</span>
          </button>
          {session.role !== "event" && (
            <button
              className={view === "templates" ? "active" : ""}
              onClick={() => {
                setView("templates");
                setActive(null);
                setCreating(false);
              }}
            >
              <LayoutTemplate size={18} />
              Design templates
            </button>
          )}
          <button
            className={view === "guide" ? "active" : ""}
            onClick={() => {
              setView("guide");
              setActive(null);
              setCreating(false);
            }}
          >
            <BookOpen size={18} />
            Event setup guide
          </button>
          {session.role !== "event" && (
            <button
              className={view === "connections" ? "active" : ""}
              onClick={() => {
                setView("connections");
                setActive(null);
                setCreating(false);
              }}
            >
              <Cloud size={18} />
              Connections
            </button>
          )}
        </nav>
        <div className="sidebar-bottom">
          <div className="local-card">
            <span className="live-dot" />
            <strong>Local-first. Always.</strong>
            <p>
              Your moments keep moving,
              <br />
              even when the internet doesn’t.
            </p>
            <span>
              <Wifi size={13} /> Event network ready
            </span>
          </div>
          <div className="profile">
            <div>PW</div>
            <span>
              <strong>PHOTO WALL</strong>
              <small>
                {session.role === "event" ? "Event operator" : "Super Admin"}
              </small>
            </span>
            <button
              title="Sign out"
              onClick={async () => {
                await save("/logout", {});
                setActive(null);
                setEvents([]);
                setCreating(false);
                setView("events");
                setSession({ authenticated: false, setupRequired: false });
              }}
            >
              <LogOut size={17} />
            </button>
          </div>
        </div>
      </aside>
      <div className="main-shell">
        <header className="topbar">
          <span>
            Workspace <span className="slash">/</span>{" "}
            {active
              ? active.name
              : creating
                ? "New event"
                : view === "events"
                  ? "Events"
                  : view === "templates"
                    ? "Design templates"
                    : view === "connections"
                      ? "Connections"
                      : "Setup guide"}
          </span>
          <div>
            <span className="server-chip">
              <span className="live-dot" /> Local server
            </span>
            <span className="avatar">PW</span>
          </div>
        </header>
        <main className={`main-content ${active || creating ? "editing" : ""}`}>
          {error && <ErrorText message={error} />}
          {active || creating ? (
            <EventEditor
              key={selectedEvent?.id || "new"}
              isSuper={session.role !== "event"}
              initial={selectedEvent}
              baseUrl={settings.baseUrl}
              driveConfigured={settings.driveConfigured}
              onBack={() => {
                setActive(null);
                setCreating(false);
                void refresh();
              }}
              onSaved={(e) => {
                setActive(e);
                setCreating(false);
                void refresh();
              }}
              notify={setMessage}
            />
          ) : view === "events" ? (
            <Dashboard
              events={events}
              onOpen={open}
              onCreate={
                session.role === "event" ? undefined : () => setCreating(true)
              }
            />
          ) : view === "connections" && session.role !== "event" ? (
            <Connections events={events} />
          ) : view === "templates" ? (
            <TemplatePage
              onCreate={(d) => {
                setCreating(true);
                setActive(null);
                sessionStorage.setItem("visra-template", JSON.stringify(d));
              }}
            />
          ) : (
            <Guide baseUrl={settings.baseUrl} />
          )}
        </main>
        <footer className="app-footer">
          <span>PHOTO WALL BY VISRA</span>
          <span>Made for the moments that bring us together.</span>
          <span>LOCAL EDITION · 0.1</span>
        </footer>
      </div>
      {message && (
        <div className="toast" role="status">
          <Check size={17} />
          {message}
          <button onClick={() => setMessage("")}>
            <X size={15} />
          </button>
        </div>
      )}
    </div>
  );
}
function Login({ setup, onLogin }: { setup: boolean; onLogin: () => void }) {
  const [loginRole, setLoginRole] = useState(
    location.pathname.startsWith("/event-login") ? "event" : "super",
  );
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  return (
    <main className="login">
      <div className="login-art">
        <Brand />
        <div>
          <p className="eyebrow">EVERY MOMENT. TOGETHER.</p>
          <h1>
            A bigger stage
            <br />
            for your memories.
          </h1>
          <p>Live photo experiences, beautifully in sync.</p>
        </div>
        <span>PRIVATE CONTROL ROOM · LOCAL FIRST</span>
      </div>
      <div className="login-form">
        <span className="eyebrow">WELCOME TO PHOTO WALL BY VISRA</span>
        <h2>Your event starts here.</h2>
        <p>Sign in to your private control room.</p>
        {setup ? (
          <div className="setup-note">
            <h3>One quick setup step</h3>
            <p>In the VS Code terminal for this project, run:</p>
            <code>npm run setup</code>
            <p>Choose your Super Admin password, then reload this page.</p>
            <button onClick={() => location.reload()}>
              <RefreshCw size={16} /> Check again
            </button>
          </div>
        ) : (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              try {
                await save("/login", {
                  password,
                  role: loginRole,
                  username: loginRole === "event" ? username : undefined,
                  eventId:
                    loginRole === "event"
                      ? location.pathname.split("/")[2]
                      : undefined,
                });
                onLogin();
              } catch (e) {
                setError((e as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            <label>
              Account type
              <select
                value={loginRole}
                onChange={(e) => setLoginRole(e.target.value)}
              >
                <option value="super">Super Admin</option>
                <option value="event">Event operator</option>
              </select>
            </label>
            {loginRole === "event" && (
              <label>
                Event user ID
                <input
                  autoComplete="username"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </label>
            )}
            <label>
              {loginRole === "event"
                ? "Event password"
                : "Super Admin password"}
              <input
                autoFocus
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>
            <ErrorText message={error} />
            <button className="primary" disabled={busy}>
              {busy ? "Signing in…" : "Enter workspace"}
              <ArrowUpRight size={17} />
            </button>
          </form>
        )}
        <small>
          <HardDrive size={14} /> Your photos stay on your event server.
        </small>
      </div>
    </main>
  );
}
function Dashboard({
  events,
  onOpen,
  onCreate,
}: {
  events: EventData[];
  onOpen: (id: string) => void;
  onCreate?: () => void;
}) {
  const [query, setQuery] = useState(""),
    [filter, setFilter] = useState("all");
  const list = events.filter(
    (e) =>
      (filter === "all" || e.status === filter) &&
      e.name.toLowerCase().includes(query.toLowerCase()),
  );
  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">THE CONTROL ROOM</p>
          <h1>
            Good moments. <em>Live.</em>
          </h1>
          <p>Bring every event to life, one photo at a time.</p>
        </div>
        {onCreate && (
          <button className="primary" onClick={onCreate}>
            <Plus size={18} /> Create event
          </button>
        )}
      </div>
      <div className="stats">
        <Stat
          icon={<Radio />}
          label="LIVE EVENTS"
          value={events.filter((e) => e.status === "live").length}
          detail="Every screen, in sync"
        />
        <Stat
          icon={<Image />}
          label="PHOTOS COLLECTED"
          value={events.reduce((n, e) => n + (e.photoCount || 0), 0)}
          detail="Moments worth keeping"
        />
        <Stat
          icon={<Monitor />}
          label="EVENT SPACES"
          value={events.length}
          detail="Independent walls & galleries"
        />
        <Stat
          icon={<HardDrive />}
          label="YOUR CONNECTION"
          value="Local first"
          detail="Built to work without internet"
        />
      </div>
      <div className="section-heading">
        <div className="tabs">
          {["all", "live", "paused", "archived"].map((f) => (
            <button
              className={filter === f ? "active" : ""}
              key={f}
              onClick={() => setFilter(f)}
            >
              {f === "all" ? "All events" : f[0].toUpperCase() + f.slice(1)}
              {f === "all" && <span>{events.length}</span>}
            </button>
          ))}
        </div>
        <label className="search">
          <Search size={16} />
          <input
            placeholder="Find an event…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
      </div>
      <div className="event-grid">
        {list.map((e) => (
          <button
            className="event-card"
            key={e.id}
            onClick={() => onOpen(e.id)}
          >
            <div
              className="event-art"
              style={{ background: e.design.backgroundColor }}
            >
              <div className="art-grain" />
              <span className={`event-status ${e.status}`}>
                <i />
                {e.status}
              </span>
              <div className="event-art-title">
                <small>
                  {e.date
                    ? new Date(e.date + "T12:00:00").toLocaleDateString(
                        undefined,
                        { month: "long", day: "numeric", year: "numeric" },
                      )
                    : "LIVE MOMENTS, BEAUTIFULLY SHARED"}
                </small>
                <h2>{e.name}</h2>
                <div className="art-cards">
                  <i />
                  <i />
                  <i />
                </div>
              </div>
              <img className="art-brand" src={logoUrl} alt={platformName} />
            </div>
            <div className="event-card-content">
              <div>
                <h3>{e.name}</h3>
                <ArrowUpRight size={18} />
              </div>
              <p>
                {e.venue || "Your event space"} · {e.design.width} ×{" "}
                {e.design.height}
              </p>
              <div className="event-card-meta">
                <span>
                  {e.mode === "local" ? (
                    <FolderOpen size={14} />
                  ) : (
                    <Cloud size={14} />
                  )}{" "}
                  {e.mode === "local"
                    ? "Local folder"
                    : e.mode === "drive"
                      ? "Google Drive"
                      : "Hybrid sync"}
                </span>
                <span>
                  <Image size={14} />
                  {e.photoCount || 0} photos
                </span>
              </div>
              {e.sourceStatus?.error && (
                <small className="source-warning">Source needs attention</small>
              )}
            </div>
          </button>
        ))}
        {onCreate && (
          <button className="create-card" onClick={onCreate}>
            <span>
              <Plus size={26} />
            </span>
            <h3>Your next great event</h3>
            <p>A fresh canvas for new memories.</p>
            <strong>
              Create an event <ArrowUpRight size={15} />
            </strong>
          </button>
        )}
      </div>
      {query && !list.length && (
        <p className="muted">No events match your search.</p>
      )}
      <div className="dashboard-note">
        <div className="note-icon">
          <Wifi size={24} />
        </div>
        <div>
          <h3>Keep the spotlight on the moment.</h3>
          <p>
            New photos go straight to the wall. No approvals, no interruptions.
            Just connect your folder and let the memories flow.
          </p>
        </div>
        <span>
          OFFLINE READY <Check size={14} />
        </span>
      </div>
    </>
  );
}
function Stat({
  icon,
  label,
  value,
  detail,
}: {
  icon: ReactNode;
  label: string;
  value: string | number;
  detail: string;
}) {
  return (
    <div className="stat">
      <div>
        <span>{label}</span>
        {icon}
      </div>
      <strong>{value}</strong>
      <small>{detail}</small>
    </div>
  );
}
function EventEditor({
  isSuper,
  initial,
  baseUrl,
  driveConfigured,
  onBack,
  onSaved,
  notify,
}: {
  isSuper: boolean;
  initial?: EventData;
  baseUrl: string;
  driveConfigured: boolean;
  onBack: () => void;
  onSaved: (e: EventData) => void;
  notify: (s: string) => void;
}) {
  const [form, setForm] = useState<EventConfig>(() => {
      if (initial) return initial;
      const e = blankEvent();
      const cached = sessionStorage.getItem("visra-template");
      if (cached) {
        e.design = JSON.parse(cached);
        sessionStorage.removeItem("visra-template");
      }
      return e;
    }),
    [tab, setTab] = useState("overview"),
    [photos, setPhotos] = useState(initial?.photos || []),
    [assets, setAssets] = useState(initial?.assets || []),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [dirty, setDirty] = useState(false),
    [customTemplates, setCustomTemplates] = useState<
      { id: string; name: string; design: Design }[]
    >([]),
    [status, setStatus] = useState(initial?.sourceStatus);
  const [createDriveFolder, setCreateDriveFolder] = useState(false);
  const [shareDriveFolder, setShareDriveFolder] = useState(true);
  const [eventUser, setEventUser] = useState(
    initial?.eventAccess?.username || "",
  );
  const [eventPassword, setEventPassword] = useState("");
  const [accessEnabled, setAccessEnabled] = useState(
    initial?.eventAccess?.enabled ?? true,
  );
  const [pickingFolder, setPickingFolder] = useState(false);
  const uploadInput = useRef<HTMLInputElement>(null);
  const patch = (value: Partial<EventConfig>) => {
    setForm((old) => ({ ...old, ...value }));
    setDirty(true);
  };
  useEffect(() => {
    void api("/admin/templates")
      .then(setCustomTemplates)
      .catch(() => {});
  }, []);
  useEffect(() => {
    void api<Media[]>("/admin/assets")
      .then(setAssets)
      .catch(() => {});
  }, []);
  async function refresh() {
    if (initial) {
      const e: EventData = await api(`/admin/events/${initial.id}`);
      setPhotos(e.photos || []);
      setAssets((old) => [
        ...new Map(
          [...old, ...(e.assets || [])].map((m) => [m.id, m]),
        ).values(),
      ]);
      setStatus(e.sourceStatus);
    }
  }
  useEffect(() => {
    if (!initial) return;
    const timer = setInterval(() => void refresh().catch(() => {}), 5000);
    return () => clearInterval(timer);
  }, [initial?.id]);
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  async function submit() {
    setBusy(true);
    setError("");
    try {
      const e: EventData = await save(
        initial ? `/admin/events/${initial.id}` : "/admin/events",
        {
          ...form,
          ...(!initial ? { createDriveFolder, shareDriveFolder } : {}),
          ...(!initial && isSuper && eventUser
            ? {
                eventAccess: {
                  username: eventUser,
                  password: eventPassword,
                  enabled: accessEnabled,
                },
              }
            : {}),
        },
        initial ? "PUT" : "POST",
      );
      setEventPassword("");
      setDirty(false);
      onSaved({ ...e, photos, assets });
      notify(
        e.driveWarning ||
          (initial
            ? "Event settings saved. Your wall is up to date."
            : "Your event is ready. Add photos to bring it to life."),
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function uploadPhotos(files: FileList | null) {
    if (!files || !initial) return;
    setBusy(true);
    let total = 0;
    try {
      for (const file of Array.from(files)) {
        await upload(initial.id, file);
        total++;
      }
      await refresh();
      notify(`${total} photo${total === 1 ? "" : "s"} added to the wall.`);
    } catch (e) {
      setError(`${total} uploaded. ${(e as Error).message}`);
      await refresh();
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <button
        className="back-button"
        onClick={() => {
          if (!dirty || confirm("Leave without saving your changes?")) onBack();
        }}
      >
        <ChevronLeft size={16} /> All events
      </button>
      <div className="page-heading editor-heading">
        <div>
          <p className="eyebrow">
            {initial ? "EVENT CONTROL" : "A NEW SPACE FOR YOUR MOMENTS"}
          </p>
          <h1>{initial ? form.name : "Create your event."}</h1>
          <p>
            {initial
              ? `${photos.length} photos · ${form.mode === "local" ? "Local folder" : form.mode === "drive" ? "Google Drive" : "Hybrid sync"}${dirty ? " · Unsaved changes" : ""}`
              : "Set the scene. Connect your photos. Go live."}
          </p>
        </div>
        <div className="button-row">
          {initial && (
            <a
              className="button"
              href={`/display/${initial.displayToken}`}
              target="_blank"
              rel="noreferrer"
            >
              <Monitor size={16} /> Open LED wall <ArrowUpRight size={14} />
            </a>
          )}
          <button className="primary" disabled={busy} onClick={submit}>
            {busy ? "Working…" : initial ? "Save changes" : "Create event"}
            <Check size={16} />
          </button>
        </div>
      </div>
      <ErrorText message={error} />
      <div className="editor-tabs">
        {[
          { id: "overview", name: "Event setup", icon: Settings2 },
          { id: "design", name: "LED design", icon: Monitor },
          { id: "photos", name: "Photo library", icon: Image },
          { id: "gallery", name: "Links & sharing", icon: Link },
          { id: "camera", name: "Camera station", icon: Camera },
        ].map((t) => (
          <button
            key={t.id}
            disabled={
              !initial && ["photos", "gallery", "camera"].includes(t.id)
            }
            className={tab === t.id ? "active" : ""}
            onClick={() => setTab(t.id)}
          >
            <t.icon size={16} />
            {t.name}
          </button>
        ))}
      </div>
      {tab === "overview" && (
        <div className="setup-grid">
          <section className="panel">
            <span className="step-badge">01</span>
            <h2>The occasion</h2>
            <p>A name, a place, a moment to remember.</p>
            <label>
              Event name
              <input
                placeholder="e.g. Allianz Annual Awards 2026"
                value={form.name}
                onChange={(e) => patch({ name: e.target.value })}
                maxLength={100}
              />
            </label>
            <div className="fields two">
              <label>
                Event date
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => patch({ date: e.target.value })}
                />
              </label>
              <label>
                Venue
                <input
                  placeholder="e.g. Dubai, UAE"
                  value={form.venue}
                  onChange={(e) => patch({ venue: e.target.value })}
                />
              </label>
            </div>
            <label>
              Event status
              <select
                value={form.status}
                onChange={(e) =>
                  patch({ status: e.target.value as EventConfig["status"] })
                }
              >
                <option value="live">Live — photos rotate automatically</option>
                <option value="paused">
                  Paused — hold display, continue collecting
                </option>
                <option value="archived">
                  Archived — stop sources and disable links
                </option>
              </select>
            </label>
            {isSuper && (
              <section className="event-account">
                <h3>Assigned event account</h3>
                <p>
                  This login can manage this event, including its design and
                  sharing, but cannot create events or access other events.
                </p>
                <label>
                  Event user ID
                  <input
                    value={eventUser}
                    autoComplete="off"
                    onChange={(e) => setEventUser(e.target.value)}
                    placeholder="event.operator"
                  />
                </label>
                <label>
                  {initial
                    ? "New event password (leave blank to keep current)"
                    : "Event account password"}
                  <input
                    type="password"
                    minLength={12}
                    autoComplete="new-password"
                    value={eventPassword}
                    onChange={(e) => setEventPassword(e.target.value)}
                  />
                </label>
                <label className="check">
                  <input
                    type="checkbox"
                    checked={accessEnabled}
                    onChange={(e) => setAccessEnabled(e.target.checked)}
                  />
                  Enable event account
                </label>
                {initial && (
                  <>
                    <button
                      type="button"
                      disabled={busy || !eventUser}
                      onClick={async () => {
                        try {
                          const e = await save(
                            `/admin/events/${initial.id}/access`,
                            {
                              username: eventUser,
                              password: eventPassword,
                              enabled: accessEnabled,
                            },
                          );
                          setEventPassword("");
                          onSaved({ ...e, photos, assets });
                          notify(
                            "Event account saved. Previous operator sessions were signed out.",
                          );
                        } catch (e) {
                          setError((e as Error).message);
                        }
                      }}
                    >
                      Save event account
                    </button>
                    <p>
                      <a
                        href={`/event-login/${initial.id}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open event login
                      </a>
                    </p>
                    <input
                      aria-label="Event login URL"
                      readOnly
                      value={`${baseUrl}/event-login/${initial.id}`}
                    />
                  </>
                )}
              </section>
            )}
            <div className="info">
              <Radio size={18} />
              <span>
                Each event runs independently, with its own LED link, photos and
                design.
              </span>
            </div>
          </section>
          <section className="panel">
            <span className="step-badge">02</span>
            <h2>Where moments arrive</h2>
            <p>Choose a photo source. You can change it later.</p>
            <section>
              <h3>Local capture + Google Drive downloads</h3>
              {!initial ? (
                <>
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={createDriveFolder}
                      onChange={(e) => setCreateDriveFolder(e.target.checked)}
                    />
                    Create a Drive folder when this event is saved
                  </label>
                  {createDriveFolder && (
                    <label className="check">
                      <input
                        type="checkbox"
                        checked={shareDriveFolder}
                        onChange={(e) => setShareDriveFolder(e.target.checked)}
                      />
                      Anyone with the folder link can view and download
                    </label>
                  )}
                </>
              ) : !form.driveFolder ? (
                <button
                  disabled={busy}
                  onClick={async () => {
                    if (dirty) {
                      setError(
                        "Save event changes before creating the Drive folder.",
                      );
                      return;
                    }
                    try {
                      const e = await save(
                        `/admin/events/${initial.id}/drive-folder`,
                        { publicRead: true },
                      );
                      setForm(e);
                      onSaved({ ...e, photos, assets });
                      notify(
                        e.driveWarning ||
                          "Drive folder created with guest download access.",
                      );
                    } catch (e) {
                      setError((e as Error).message);
                    }
                  }}
                >
                  Create Drive folder with guest download access
                </button>
              ) : (
                <p>Connected folder: {form.driveFolder}</p>
              )}
              <small>
                Requires Google Drive connection and internet. Local photos
                appear immediately; uploads retry when internet returns.
              </small>
            </section>
            <div className="source-options">
              {(
                [
                  { id: "local", name: "Local folder", icon: FolderOpen },
                  { id: "drive", name: "Google Drive", icon: Cloud },
                  { id: "hybrid", name: "Hybrid", icon: RefreshCw },
                ] as const
              ).map((m) => (
                <button
                  key={m.id}
                  className={form.mode === m.id ? "active" : ""}
                  onClick={() => patch({ mode: m.id })}
                >
                  <m.icon size={22} />
                  {m.name}
                </button>
              ))}
            </div>
            {form.mode !== "drive" && (
              <div>
                <label>
                  Watched folder on the server
                  <input
                    placeholder="/Users/yourname/Pictures/Event Photos"
                    value={form.folder}
                    onChange={(e) => patch({ folder: e.target.value })}
                  />
                  <small>
                    Optional. Use an existing folder on this computer or a
                    mounted NAS. New JPEG, PNG and WebP files appear
                    automatically.
                  </small>
                </label>
                <button type="button" onClick={() => setPickingFolder(true)}>
                  <FolderOpen size={16} /> Browse folder
                </button>
                {pickingFolder && (
                  <FolderPicker
                    initialPath={form.folder}
                    onClose={() => setPickingFolder(false)}
                    onSelect={(folder) => {
                      patch({ folder });
                      setPickingFolder(false);
                    }}
                  />
                )}
              </div>
            )}
            {form.mode !== "local" && (
              <>
                <label className="check">
                  <input
                    type="checkbox"
                    checked={form.driveImport !== false}
                    onChange={(e) =>
                      patch({ driveImport: e.target.checked })
                    }
                  />
                  Import photos added in this Drive folder into the library
                </label>
                <label>
                  Google Drive folder ID
                  <input
                    placeholder="The ID from your Drive folder URL"
                    value={form.driveFolder}
                    onChange={(e) => patch({ driveFolder: e.target.value })}
                  />
                </label>
                <div className={driveConfigured ? "info" : "warning"}>
                  {driveConfigured
                    ? "Google Drive credentials are configured. New photos are checked every 15 seconds and cached locally for offline playback."
                    : "Google Drive needs a one-time connection. See docs/GOOGLE-DRIVE.md. Local uploads will still work."}
                </div>
              </>
            )}
            <div className="info">
              <HardDrive size={18} />
              <span>
                {form.mode === "hybrid"
                  ? "Local photos display immediately. Drive sync retries when internet returns."
                  : "Your photo wall always keeps a local copy for reliable playback."}
              </span>
            </div>
            {status?.error && <div className="warning">{status.error}</div>}
            {status && status.jobs.length > 0 && (
              <p className="muted">
                Drive sync:{" "}
                {status.jobs.map((j) => `${j.count} ${j.status}`).join(" · ")}
              </p>
            )}
          </section>
          <section className="setup-preview panel">
            <div>
              <span className="step-badge">03</span>
              <h2>Set the scene</h2>
              <p>Make your wall as individual as your event.</p>
              <button onClick={() => setTab("design")}>
                Customize LED design <ArrowUpRight size={16} />
              </button>
            </div>
            <Wall
              name={form.name}
              design={form.design}
              photos={photos}
              assets={assets}
            />
          </section>
        </div>
      )}
      {tab === "overview" && initial && isSuper && (
        <EventCleanup
          event={initial}
          onDeleted={onBack}
          onCleared={async () => {
            const e = await api(`/admin/events/${initial.id}`);
            setPhotos([]);
            setForm(e);
            setDirty(false);
            onSaved(e);
          }}
          notify={notify}
        />
      )}
      {tab === "design" && (
        <DesignEditor
          name={form.name}
          design={form.design}
          onChange={(d) => patch({ design: d })}
          photos={photos}
          assets={assets}
          eventId={initial?.id}
          onAsset={(m) => setAssets((a) => [...a, m])}
          notify={notify}
          customTemplates={customTemplates}
        />
      )}
      {tab === "photos" && initial && (
        <>
          <div className="photo-heading">
            <div>
              <h2>Every photo, straight to the wall.</h2>
              <p>Uploaded photos appear automatically. No approval needed.</p>
            </div>
            <button
              className="primary"
              disabled={busy || form.status === "archived"}
              onClick={() => uploadInput.current?.click()}
            >
              <Upload size={16} /> Upload photos
            </button>
          </div>
          <div
            className="dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              if (!busy) void uploadPhotos(e.dataTransfer.files);
            }}
            onClick={() => !busy && uploadInput.current?.click()}
          >
            <Upload size={26} />
            <strong>
              {busy
                ? "Adding your moments…"
                : "Drop photos here, or click to browse"}
            </strong>
            <span>JPEG, PNG or WebP · up to 80 MB each</span>
          </div>
          <input
            ref={uploadInput}
            hidden
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => {
              void uploadPhotos(e.target.files);
              e.target.value = "";
            }}
          />
          <div className="photo-library">
            {[...photos].reverse().map((p) => (
              <a href={p.originalUrl} key={p.id}>
                <img src={p.url} alt={p.name} loading="lazy" />
                <span>{p.name}</span>
              </a>
            ))}
          </div>
        </>
      )}
      {tab === "gallery" && initial && (
        <GallerySettings
          event={initial}
          form={form}
          patch={patch}
          baseUrl={baseUrl}
          notify={notify}
          onRotated={onSaved}
        />
      )}
      {tab === "camera" && initial && (
        <CameraStation eventId={initial.id} onUploaded={() => void refresh()} />
      )}
    </>
  );
}
function GallerySettings({
  event,
  form,
  patch,
  baseUrl,
  notify,
  onRotated,
}: {
  event: EventData;
  form: EventConfig;
  patch: (p: Partial<EventConfig>) => void;
  baseUrl: string;
  notify: (s: string) => void;
  onRotated: (e: EventData) => void;
}) {
  const [qr, setQr] = useState("");
  const [captureQr, setCaptureQr] = useState("");
  const captureUrl = `${baseUrl.replace(/\/$/, "")}/capture/${event.captureToken}`;
  useEffect(() => {
    void QRCode.toDataURL(captureUrl, {
      width: 600,
      margin: 3,
      color: { dark: "#0a1740ff", light: "#ffffffff" },
    }).then(setCaptureQr);
  }, [captureUrl]);
  const display = `${baseUrl.replace(/\/$/, "")}/display/${event.displayToken}`,
    gallery =
      form.guestDestination === "drive" && form.driveFolder
        ? `https://drive.google.com/drive/folders/${form.driveFolder}`
        : `${(form.guestBaseUrl || baseUrl).replace(/\/$/, "")}/gallery/${event.galleryToken}`;
  useEffect(() => {
    void QRCode.toDataURL(gallery, {
      width: 600,
      margin: 3,
      color: { dark: "#0a1740ff", light: "#ffffffff" },
    }).then(setQr);
  }, [gallery]);
  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      notify("Link copied.");
    } catch {
      prompt("Copy this link:", text);
    }
  }
  return (
    <div className="setup-grid">
      <section className="panel">
        <p className="eyebrow">YOUR DEDICATED SCREEN</p>
        <h2>The LED wall link</h2>
        <p>
          Open this link on the computer connected to your LED processor. Click
          the screen to enter fullscreen.
        </p>
        <div className="link-field">
          <input readOnly value={display} />
          <button onClick={() => void copy(display)} title="Copy LED link">
            <Copy size={16} />
          </button>
        </div>
        <a className="button" target="_blank" rel="noreferrer" href={display}>
          <Monitor size={16} /> Open display
        </a>
        <hr />
        <h3>Refresh access links</h3>
        <p>
          Rotate the LED and gallery links if you need to stop sharing them.
          Capture links are managed separately. Update all LED screens and
          printed QR codes afterward.
        </p>
        <button
          className="danger"
          onClick={async () => {
            if (
              !confirm(
                "Replace both links? Existing LED and gallery links will stop working.",
              )
            )
              return;
            try {
              const e = await save(`/admin/events/${event.id}/rotate`, {});
              onRotated(e);
              notify(
                "Links rotated. Use the new links for your screens and QR codes.",
              );
            } catch (e) {
              notify((e as Error).message);
            }
          }}
        >
          <RefreshCw size={15} /> Rotate event links
        </button>
      </section>
      <section className="panel capture-sharing">
        <p className="eyebrow">A CAMERA STATION ON ANY DEVICE</p>
        <h2>Event capture link</h2>
        <p>
          Open a separate capture screen on a phone, tablet or computer. This
          link allows photo submissions to this event without a Super Admin
          account.
        </p>
        <label className="toggle-label">
          <span>
            <strong>Enable capture link</strong>
            <small>
              Anyone with this link can add photos directly to the wall.
            </small>
          </span>
          <input
            type="checkbox"
            checked={form.captureEnabled ?? false}
            onChange={(e) => patch({ captureEnabled: e.target.checked })}
          />
        </label>
        {form.captureEnabled !== event.captureEnabled && (
          <p aria-live="polite">
            Save changes to {form.captureEnabled ? "activate" : "disable"} the
            capture link.
          </p>
        )}
        {form.captureEnabled && event.captureEnabled && (
          <>
            <div className="qr-row">
              {captureQr && (
                <img src={captureQr} alt="QR code for event capture" />
              )}
              <div>
                <h3>Scan. Capture. Submit.</h3>
                <p>
                  Save changes before sharing. Use the server’s LAN address, not
                  localhost, on other devices.
                </p>
                <a
                  className="button"
                  href={captureQr}
                  download={`visra-capture-${event.id}.png`}
                >
                  Download capture QR
                </a>
              </div>
            </div>
            <div className="link-field">
              <input
                aria-label="Event capture URL"
                readOnly
                value={captureUrl}
              />
              <button
                title="Copy capture link"
                onClick={() => void copy(captureUrl)}
              >
                <Copy size={16} />
              </button>
            </div>
            <a
              className="button"
              href={captureUrl}
              target="_blank"
              rel="noreferrer"
            >
              <Camera size={16} /> Open capture screen{" "}
              <ArrowUpRight size={14} />
            </a>
            <p className="capture-help">
              Phone camera/file selection works where supported by the device.
              Embedded live camera preview requires HTTPS or localhost. Devices
              must reach this event server.
            </p>
          </>
        )}
        <button
          className="danger"
          onClick={async () => {
            if (
              !confirm(
                "Replace the capture link? The previous capture URL and QR will stop accepting photos. LED and gallery links will stay the same.",
              )
            )
              return;
            try {
              onRotated(
                await save(`/admin/events/${event.id}/capture/rotate`, {}),
              );
              notify("Capture link replaced. Share the new link or QR.");
            } catch (e) {
              notify((e as Error).message);
            }
          }}
        >
          <RefreshCw size={15} /> Replace capture link
        </button>
      </section>
      <section className="panel">
        <p className="eyebrow">LET GUESTS KEEP THE MOMENT</p>
        <h2>Guest gallery & QR</h2>
        <label>
          Guest download destination
          <select
            value={form.guestDestination ?? "local"}
            onChange={(e) =>
              patch({ guestDestination: e.target.value as "local" | "drive" })
            }
          >
            <option value="local">Local event gallery</option>
            <option value="drive" disabled={!form.driveFolder}>
              Google Drive folder
            </option>
          </select>
        </label>
        {form.guestDestination === "drive" && (
          <>
            <p>
              Guests need internet. Only photos already uploaded to Drive are
              available there.
            </p>
            {!form.drivePublic && (
              <button
                onClick={async () => {
                  try {
                    const e = await save(
                      `/admin/events/${event.id}/drive-share`,
                      {},
                    );
                    patch({ drivePublic: true });
                    onRotated(e);
                    notify(
                      "Anyone with the Drive folder link can now view and download.",
                    );
                  } catch (e) {
                    notify((e as Error).message);
                  }
                }}
              >
                Enable anyone-with-link guest access
              </button>
            )}
          </>
        )}

        {form.guestDestination !== "drive" && (
          <>
            <label className="toggle-label">
              <span>
                <strong>Enable guest gallery</strong>
                <small>
                  Anyone with this link can view and download photos.
                </small>
              </span>
              <input
                type="checkbox"
                checked={form.galleryEnabled}
                onChange={(e) => patch({ galleryEnabled: e.target.checked })}
              />
            </label>
            <label>
              Guest-facing server URL (optional)
              <input
                placeholder={baseUrl}
                value={form.guestBaseUrl}
                onChange={(e) => patch({ guestBaseUrl: e.target.value })}
              />
              <small>
                Use your server’s LAN address for event Wi-Fi, or its HTTPS
                address for internet access. Changing this field does not
                publish or host the gallery.
              </small>
            </label>
            {form.galleryEnabled !== event.galleryEnabled && (
              <p aria-live="polite">
                Save changes to {form.galleryEnabled ? "activate" : "disable"}{" "}
                the guest gallery.
              </p>
            )}
            {!form.galleryEnabled && !event.galleryEnabled && (
              <p>
                The guest gallery is off. Enable it and save changes to share
                photos with guests.
              </p>
            )}
          </>
        )}
        {((form.guestDestination === "drive" &&
          form.driveFolder &&
          form.drivePublic) ||
          (form.guestDestination !== "drive" &&
            form.galleryEnabled &&
            event.galleryEnabled)) && (
          <>
            <div className="qr-row">
              {qr && <img src={qr} alt="QR code for guest gallery" />}
              <div>
                <h3>Scan. Find. Download.</h3>
                <p>
                  {form.guestDestination === "drive"
                    ? "Guests can open this Drive folder over any internet connection."
                    : "Save changes before sharing. Guests on the local gallery must join the event Wi-Fi."}
                </p>
                <a
                  className="button"
                  href={qr}
                  download={`visra-${event.id}-qr.png`}
                >
                  Download QR
                </a>
              </div>
            </div>
            <div className="link-field">
              <input readOnly value={gallery} />
              <button
                onClick={() => void copy(gallery)}
                title="Copy gallery link"
              >
                <Copy size={16} />
              </button>
            </div>
            <a
              className="button"
              href={gallery}
              target="_blank"
              rel="noreferrer"
            >
              Open guest gallery <ArrowUpRight size={15} />
            </a>
          </>
        )}
      </section>
    </div>
  );
}
function TemplatePage({ onCreate }: { onCreate: (d: Design) => void }) {
  const [saved, setSaved] = useState<
    { id: string; name: string; design: Design }[]
  >([]);
  useEffect(() => {
    void api("/admin/templates").then(setSaved);
  }, []);
  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">DESIGNED FOR YOUR MOMENTS</p>
          <h1>A little inspiration.</h1>
          <p>Start with a look. Make it entirely your own.</p>
        </div>
      </div>
      <div className="event-grid">
        {[...templates, ...saved].map((t) => (
          <button
            className="event-card template-card"
            key={t.id}
            onClick={() => onCreate(t.design)}
          >
            <Wall name={t.name} design={t.design} photos={[]} assets={[]} />
            <div className="event-card-content">
              <h3>{t.name}</h3>
              <p>
                Use this template <ArrowUpRight size={14} />
              </p>
            </div>
          </button>
        ))}
      </div>
    </>
  );
}
function Guide({ baseUrl }: { baseUrl: string }) {
  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">SHOWTIME, WITHOUT THE GUESSWORK</p>
          <h1>A smooth event starts here.</h1>
          <p>Your local setup in four simple steps.</p>
        </div>
      </div>
      <div className="guide-grid">
        {[
          {
            title: "Bring your own network",
            text: "Connect the Visra server, photo station and LED computer to the same router. Internet is optional for the local wall and gallery.",
          },
          {
            title: "Create an event",
            text: "Choose a photo source, add the event name and customize your screen. A folder must be an existing absolute path on the server computer.",
          },
          {
            title: "Connect the LED screen",
            text: "Open the event’s dedicated display link on the LED computer. Click the screen for fullscreen. Keep the server awake and connected to power.",
          },
          {
            title: "Add your first moments",
            text: "Export JPEG, PNG or WebP images to the watched folder, upload them in Photo Library, or use Camera Station. They appear automatically.",
          },
        ].map((s, i) => (
          <section className="panel" key={s.title}>
            <span className="step-badge">0{i + 1}</span>
            <h2>{s.title}</h2>
            <p>{s.text}</p>
          </section>
        ))}
      </div>
      <div className="panel">
        <h2>Before doors open</h2>
        <p>
          Set PUBLIC_BASE_URL in .env to the server’s LAN address so copied
          links and QR codes work on other devices. Restart Visra after changing
          .env.
        </p>
        <p>
          Current base URL: <code>{baseUrl}</code>
        </p>
        <p>
          Test a real photo, the LED output, a guest download, and a network
          reconnect. For camera access from another computer, use HTTPS. See
          docs/RUNBOOK.md and docs/GOOGLE-DRIVE.md in this project for the full
          setup.
        </p>
      </div>
    </>
  );
}
